import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import GithubIcon from '@/assets/icons/landing-github.svg?react';
import GoogleIcon from '@/assets/icons/landing-google.svg?react';
import SsoKeyIcon from '@/assets/icons/landing-sso-key.svg?react';
import RotatingRingIcon from '@/shared/components/Button/RotatingRingIcon';

import useSocial from '../../containers/SocialSignInList/use-social';

import styles from './LandingSocialButtons.module.scss';

type ButtonProps = {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly isLoading: boolean;
  readonly onClick: () => void;
};

const SocialButton = ({ icon, label, isLoading, onClick }: ButtonProps) => (
  <button type="button" className={styles.button} disabled={isLoading} onClick={onClick}>
    {isLoading ? (
      <span className={styles.loadingIcon}>
        <RotatingRingIcon />
      </span>
    ) : (
      <span className={styles.icon}>{icon}</span>
    )}
    <span>{label}</span>
  </button>
);

type Props = {
  readonly isSingleSignOnEnabled?: boolean;
};

const LandingSocialButtons = ({ isSingleSignOnEnabled }: Props) => {
  const { invokeSocialSignIn, socialConnectors } = useSocial();
  const navigate = useNavigate();
  const [loadingTarget, setLoadingTarget] = useState<string>();

  const handleSocialClick = useCallback(
    async (target: string) => {
      const connector = socialConnectors.find((item) => item.target === target);

      if (!connector) {
        return;
      }

      setLoadingTarget(target);
      await invokeSocialSignIn(connector);
      setLoadingTarget(undefined);
    },
    [socialConnectors, invokeSocialSignIn]
  );

  const handleSsoClick = useCallback(() => {
    navigate('/single-sign-on/email');
  }, [navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <SocialButton
          icon={<GoogleIcon />}
          label="Continue with Google"
          isLoading={loadingTarget === 'google'}
          onClick={() => {
            void handleSocialClick('google');
          }}
        />
        <SocialButton
          icon={<GithubIcon />}
          label="Continue with Github"
          isLoading={loadingTarget === 'github'}
          onClick={() => {
            void handleSocialClick('github');
          }}
        />
      </div>
      {isSingleSignOnEnabled && (
        <SocialButton
          icon={<SsoKeyIcon />}
          label="Continue with Enterprise SSO"
          isLoading={false}
          onClick={handleSsoClick}
        />
      )}
    </div>
  );
};

export default LandingSocialButtons;
